import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { RecurrenceConfigError } from '../errors';
import type { Frequency } from '../types';

dayjs.extend(utc);

/** The frequency/interval to advance by and the period index `n`. */
interface ExpandOptions {
  freq: Frequency;
  interval: number;
  n: number;
}

/**
 * Fixed / date-seeded recurrence model (MVP; see the "Recurrence models"
 * section of CLAUDE.md for the planned siblings).
 *
 * Advance a single seed forward by `n` periods. Seeds must be calendar days at
 * midnight UTC — an invalid or non-midnight seed throws `RecurrenceConfigError`
 * rather than being silently normalised. All reads use `dayjs.utc` so the
 * runtime timezone can't shift which day we land on.
 *
 * - `DAILY`: seed + `n * interval` days.
 * - `WEEKLY`: seed + `n * interval` weeks, landing on the seed's weekday.
 * - `MONTHLY`: target month = seedMonth + `n * interval`; day = `min(seedDay, daysInTargetMonth)`.
 * - `YEARLY`: target year = seedYear + `n * interval`, same month, day clamped (Feb 29 → Feb 28).
 *
 * Only the month/year cases clamp: there is no month-end for a day or a week to
 * run past, so 31 Jan weekly is 7 Feb rather than the month case's 28 Feb. The
 * month/year clamp is computed independently per period, so a short month never
 * poisons later periods.
 */
export const expandFixed = (
  seed: Date,
  { freq, interval, n }: ExpandOptions
): Date => {
  const s = dayjs.utc(seed);
  if (!s.isValid()) {
    throw new RecurrenceConfigError('Seed date is invalid');
  }
  if (!s.isSame(s.startOf('day'))) {
    throw new RecurrenceConfigError(
      'Seed dates must be midnight UTC (calendar days); got a time-of-day component'
    );
  }
  const seedDay = s.date();

  // Move to the 1st before setting year/month to avoid day-overflow rollover
  // (e.g. setting month to Feb while the date is 31 would spill into March).
  const anchorAndClamp = (targetYear: number, targetMonth: number): Dayjs => {
    const anchored = s.date(1).year(targetYear).month(targetMonth);

    return anchored
      .date(Math.min(seedDay, anchored.daysInMonth()))
      .startOf('day');
  };

  let result: Dayjs;

  switch (freq) {
    case 'DAILY': {
      result = s.add(n * interval, 'day');
      break;
    }
    case 'WEEKLY': {
      result = s.add(n * interval, 'week');
      break;
    }
    case 'MONTHLY': {
      const totalMonths = s.month() + n * interval;
      result = anchorAndClamp(
        s.year() + Math.floor(totalMonths / 12),
        ((totalMonths % 12) + 12) % 12
      );
      break;
    }
    case 'YEARLY': {
      result = anchorAndClamp(s.year() + n * interval, s.month());
      break;
    }
    default: {
      // Exhaustiveness guard: a new Frequency must fail tsc here, not silently
      // expand under another frequency's math.
      const unhandled: never = freq;
      throw new RecurrenceConfigError(
        `Unhandled frequency: ${String(unhandled)}`
      );
    }
  }

  // A large INTERVAL can push the year past JS's max Date range (~275760 AD),
  // giving an Invalid Date whose NaN time would slip past the UNTIL guard in
  // computeNextWindow. Fail at the source instead.
  if (!result.isValid()) {
    throw new RecurrenceConfigError(
      `Recurrence window date out of range (INTERVAL too large): freq=${freq}, interval=${interval}, period=${n}`
    );
  }

  return result.toDate();
};
